import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star, Globe, Menu, X } from "lucide-react";
import { BRAND } from "../mock";
import { useJobModal } from "../context/JobModalContext";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { label: "For families", to: "/for-families" },
  { label: "For caregivers", to: "/for-caregivers" },
  { label: "Find jobs", to: "/jobs" },
  { label: "How it works", to: "/how-it-works" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { openJobModal } = useJobModal();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const dash = user ? (user.role === "admin" ? "/admin" : user.role === "caregiver" ? "/caregiver" : "/dashboard") : "/";
  const doLogout = () => { logout(); navigate("/"); };

  return (
    <header className="sticky top-0 z-50">
      {/* Top bar */}
      <div className="bg-[#17161d] text-[#f2ece7] text-[13px]">
        <div className="max-w-7xl mx-auto px-5 h-9 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Star className="w-3.5 h-3.5 text-[#d99aa6] fill-[#d99aa6]" />
            <span className="tracking-tight">Trusted by families across the US</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-[#e9c3cb]">{BRAND.freeTrial}</span>
            <span className="hidden sm:inline text-white/20">|</span>
            <a href="mailto:hello@simplysitter.us" className="hover:text-white transition-colors">Need help?</a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="bg-white/90 backdrop-blur-md border-b border-black/5">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl tracking-tight">
            <span className="text-[#1f1a1b]">Simply</span>
            <span className="text-[#7c2233]">Sitter</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8 text-[15px] text-[#3a3436]">
            {NAV.map((n) => (
              n.to ? (
                <Link key={n.label} to={n.to} className="relative py-1 hover:text-[#7c2233] transition-colors after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:w-0 after:bg-[#7c2233] after:transition-all hover:after:w-full">
                  {n.label}
                </Link>
              ) : (
                <a key={n.label} href={n.href} className="relative py-1 hover:text-[#7c2233] transition-colors after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:w-0 after:bg-[#7c2233] after:transition-all hover:after:w-full">
                  {n.label}
                </a>
              )
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-1.5 text-[13px] text-[#5a5254] border border-black/10 rounded-full px-2.5 py-1">
              <Globe className="w-3.5 h-3.5" />
              <span>EN</span>
            </div>
            <a href="#login" className="hidden md:inline text-[15px] text-[#3a3436] hover:text-[#7c2233] transition-colors" style={{ display: user ? "none" : undefined }} onClick={(e) => { e.preventDefault(); navigate("/auth?mode=login"); }}>Log in</a>
            {user ? (
              <>
                <Link to={dash} className="hidden md:inline text-[15px] font-medium text-[#7c2233] hover:underline">Dashboard</Link>
                <button onClick={doLogout} className="inline-flex items-center rounded-lg border border-[#7c2233]/30 text-[#7c2233] text-[14px] font-medium px-4 py-2.5 hover:bg-[#faf1f2] transition-colors">Log out</button>
              </>
            ) : (
              <button onClick={() => openJobModal({})} className="inline-flex items-center rounded-lg bg-[#7c2233] hover:bg-[#661b29] text-white text-[14px] font-medium px-4 py-2.5 shadow-sm transition-colors">
                Post a job free
              </button>
            )}
            <button className="lg:hidden ml-1 text-[#1f1a1b]" onClick={() => setOpen(!open)} aria-label="Menu">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-black/5 bg-white px-5 py-3 flex flex-col gap-1">
            {NAV.map((n) => (
              n.to ? (
                <Link key={n.label} to={n.to} onClick={() => setOpen(false)} className="py-2.5 text-[15px] text-[#3a3436] border-b border-black/5">{n.label}</Link>
              ) : (
                <a key={n.label} href={n.href} onClick={() => setOpen(false)} className="py-2.5 text-[15px] text-[#3a3436] border-b border-black/5">{n.label}</a>
              )
            ))}
            {user ? (
              <>
                <Link to={dash} onClick={() => setOpen(false)} className="py-2.5 text-[15px] font-medium text-[#7c2233] border-b border-black/5" data-testid="mobile-dashboard-link">Dashboard</Link>
                <button onClick={() => { setOpen(false); doLogout(); }} data-testid="mobile-logout-btn" className="mt-2 w-full inline-flex items-center justify-center rounded-lg border border-[#7c2233]/30 text-[#7c2233] text-[15px] font-medium px-4 py-3 hover:bg-[#faf1f2] transition-colors">Log out</button>
              </>
            ) : (
              <>
                <button onClick={() => { setOpen(false); navigate("/auth?mode=login"); }} data-testid="mobile-login-btn" className="w-full text-center py-3 text-[15px] font-medium text-[#3a3436] border border-black/10 rounded-lg mt-2 hover:bg-black/[0.03] transition-colors">Log in</button>
                <button onClick={() => { setOpen(false); openJobModal({}); }} data-testid="mobile-post-job-btn" className="w-full inline-flex items-center justify-center rounded-lg bg-[#7c2233] hover:bg-[#661b29] text-white text-[15px] font-medium px-4 py-3 shadow-sm transition-colors mt-2">
                  Post a job free
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
